import apiClient from './client';

export enum EquipmentType {
  CAGE = 'CAGE',
  AVIARY = 'AVIARY',
  BREEDING_BOX = 'BREEDING_BOX',
  FEEDER = 'FEEDER',
  WATERER = 'WATERER',
  PERCH = 'PERCH',
  NESTING_BOX = 'NESTING_BOX',
  INCUBATOR = 'INCUBATOR',
  BROODER = 'BROODER',
  SCALE = 'SCALE',
  THERMOMETER = 'THERMOMETER',
  HYGROMETER = 'HYGROMETER',
  LIGHTING = 'LIGHTING',
  HEATING = 'HEATING',
  VENTILATION = 'VENTILATION',
  CLEANING_EQUIPMENT = 'CLEANING_EQUIPMENT',
  TRANSPORT_CARRIER = 'TRANSPORT_CARRIER',
  MEDICAL_EQUIPMENT = 'MEDICAL_EQUIPMENT',
  TOOLS = 'TOOLS',
  OTHER = 'OTHER',
}

export enum EquipmentStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  MAINTENANCE = 'MAINTENANCE',
  REPAIR = 'REPAIR',
  RETIRED = 'RETIRED',
  DISPOSED = 'DISPOSED',
}

export enum EquipmentCondition {
  EXCELLENT = 'EXCELLENT',
  GOOD = 'GOOD',
  FAIR = 'FAIR',
  POOR = 'POOR',
  CRITICAL = 'CRITICAL',
}

export enum MaintenanceFrequency {
  DAILY = 'DAILY',
  WEEKLY = 'WEEKLY',
  MONTHLY = 'MONTHLY',
  QUARTERLY = 'QUARTERLY',
  SEMI_ANNUALLY = 'SEMI_ANNUALLY',
  ANNUALLY = 'ANNUALLY',
  AS_NEEDED = 'AS_NEEDED',
}

export interface Equipment {
  id: string;
  name: string;
  type: EquipmentType;
  model?: string;
  serialNumber?: string;
  assetTag?: string;
  status: EquipmentStatus;
  condition: EquipmentCondition;
  location?: string;
  description?: string;
  manufacturer?: string;
  purchaseDate?: Date;
  purchasePrice: number;
  currentValue?: number;
  depreciationRate: number;
  usefulLifeYears: number;
  warrantyExpiration?: Date;
  supplierId?: string;
  supplier?: string;
  maintenanceFrequency: MaintenanceFrequency;
  lastMaintenanceDate?: Date;
  nextMaintenanceDate?: Date;
  maintenanceInstructions?: string;
  operatingInstructions?: string;
  safetyNotes?: string;
  capacity?: string;
  dimensions?: string;
  weight?: number;
  powerRequirements?: string;
  inUse: boolean;
  qrCode?: string;
  photos?: string[];
  notes?: string;
  createdAt: Date;
  updatedAt: Date;
  isUnderWarranty: boolean;
  isMaintenanceDue: boolean;
  isMaintenanceOverdue: boolean;
  ageInYears: number;
  depreciatedValue: number;
}

export interface CreateEquipmentDto {
  name: string;
  type: EquipmentType;
  model?: string;
  serialNumber?: string;
  assetTag?: string;
  status?: EquipmentStatus;
  condition?: EquipmentCondition;
  location?: string;
  description?: string;
  manufacturer?: string;
  purchaseDate?: Date;
  purchasePrice: number;
  currentValue?: number;
  depreciationRate?: number;
  usefulLifeYears?: number;
  warrantyExpiration?: Date;
  supplierId?: string;
  supplier?: string;
  maintenanceFrequency?: MaintenanceFrequency;
  lastMaintenanceDate?: Date;
  nextMaintenanceDate?: Date;
  maintenanceInstructions?: string;
  operatingInstructions?: string;
  safetyNotes?: string;
  capacity?: string;
  dimensions?: string;
  weight?: number;
  powerRequirements?: string;
  inUse?: boolean;
  qrCode?: string;
  photos?: string[];
  notes?: string;
}

export interface UpdateEquipmentDto extends Partial<CreateEquipmentDto> {}

export interface EquipmentFilters {
  search?: string;
  type?: EquipmentType;
  status?: EquipmentStatus;
  condition?: EquipmentCondition;
  location?: string;
  manufacturer?: string;
  supplierId?: string;
  supplier?: string;
  inUse?: boolean;
  maintenanceDue?: boolean;
  underWarranty?: boolean;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface EquipmentResponse {
  data: Equipment[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface EquipmentStats {
  totalItems: number;
  activeItems: number;
  maintenanceItems: number;
  repairItems: number;
  maintenanceDueItems: number;
  maintenanceOverdueItems: number;
  underWarrantyItems: number;
  totalValue: number;
  totalDepreciatedValue: number;
  byType: Record<string, number>;
  byStatus: Record<string, number>;
  byCondition: Record<string, number>;
  byLocation: Record<string, number>;
}

export interface MaintenanceRecord {
  equipmentId: string;
  maintenanceDate: Date;
  maintenanceType?: string;
  description?: string;
  performedBy?: string;
  cost?: number;
  nextMaintenanceDate?: Date;
  notes?: string;
}

// Label functions
export const getEquipmentTypeLabel = (type: EquipmentType): string => {
  const labels: Record<EquipmentType, string> = {
    [EquipmentType.CAGE]: 'Cage',
    [EquipmentType.AVIARY]: 'Aviary',
    [EquipmentType.BREEDING_BOX]: 'Breeding Box',
    [EquipmentType.FEEDER]: 'Feeder',
    [EquipmentType.WATERER]: 'Waterer',
    [EquipmentType.PERCH]: 'Perch',
    [EquipmentType.NESTING_BOX]: 'Nesting Box',
    [EquipmentType.INCUBATOR]: 'Incubator',
    [EquipmentType.BROODER]: 'Brooder',
    [EquipmentType.SCALE]: 'Scale',
    [EquipmentType.THERMOMETER]: 'Thermometer',
    [EquipmentType.HYGROMETER]: 'Hygrometer',
    [EquipmentType.LIGHTING]: 'Lighting',
    [EquipmentType.HEATING]: 'Heating',
    [EquipmentType.VENTILATION]: 'Ventilation',
    [EquipmentType.CLEANING_EQUIPMENT]: 'Cleaning Equipment',
    [EquipmentType.TRANSPORT_CARRIER]: 'Transport Carrier',
    [EquipmentType.MEDICAL_EQUIPMENT]: 'Medical Equipment',
    [EquipmentType.TOOLS]: 'Tools',
    [EquipmentType.OTHER]: 'Other',
  };
  return labels[type] || type;
};

export const getEquipmentStatusLabel = (status: EquipmentStatus): string => {
  const labels: Record<EquipmentStatus, string> = {
    [EquipmentStatus.ACTIVE]: 'Active',
    [EquipmentStatus.INACTIVE]: 'Inactive',
    [EquipmentStatus.MAINTENANCE]: 'Maintenance',
    [EquipmentStatus.REPAIR]: 'Repair',
    [EquipmentStatus.RETIRED]: 'Retired',
    [EquipmentStatus.DISPOSED]: 'Disposed',
  };
  return labels[status] || status;
};

export const getEquipmentConditionLabel = (condition: EquipmentCondition): string => {
  const labels: Record<EquipmentCondition, string> = {
    [EquipmentCondition.EXCELLENT]: 'Excellent',
    [EquipmentCondition.GOOD]: 'Good',
    [EquipmentCondition.FAIR]: 'Fair',
    [EquipmentCondition.POOR]: 'Poor',
    [EquipmentCondition.CRITICAL]: 'Critical',
  };
  return labels[condition] || condition;
};

export const getMaintenanceFrequencyLabel = (frequency: MaintenanceFrequency): string => {
  const labels: Record<MaintenanceFrequency, string> = {
    [MaintenanceFrequency.DAILY]: 'Daily',
    [MaintenanceFrequency.WEEKLY]: 'Weekly',
    [MaintenanceFrequency.MONTHLY]: 'Monthly',
    [MaintenanceFrequency.QUARTERLY]: 'Quarterly',
    [MaintenanceFrequency.SEMI_ANNUALLY]: 'Semi-Annually',
    [MaintenanceFrequency.ANNUALLY]: 'Annually',
    [MaintenanceFrequency.AS_NEEDED]: 'As Needed',
  };
  return labels[frequency] || frequency;
};

// Color functions
export const getEquipmentStatusColor = (status: EquipmentStatus): string => {
  const colors: Record<EquipmentStatus, string> = {
    [EquipmentStatus.ACTIVE]: 'green',
    [EquipmentStatus.INACTIVE]: 'gray',
    [EquipmentStatus.MAINTENANCE]: 'yellow',
    [EquipmentStatus.REPAIR]: 'red',
    [EquipmentStatus.RETIRED]: 'gray',
    [EquipmentStatus.DISPOSED]: 'red',
  };
  return colors[status] || 'gray';
};

export const getEquipmentConditionColor = (condition: EquipmentCondition): string => {
  const colors: Record<EquipmentCondition, string> = {
    [EquipmentCondition.EXCELLENT]: 'green',
    [EquipmentCondition.GOOD]: 'blue',
    [EquipmentCondition.FAIR]: 'yellow',
    [EquipmentCondition.POOR]: 'orange',
    [EquipmentCondition.CRITICAL]: 'red',
  };
  return colors[condition] || 'gray';
};

// Utility functions
export const formatCurrency = (amount: number): string => {
  // Handle NaN and invalid numbers
  const validAmount = isNaN(amount) || !isFinite(amount) ? 0 : amount;
  return new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: 'ZAR',
  }).format(validAmount);
};

export const formatDate = (date?: Date): string => {
  if (!date) return 'N/A';
  return new Date(date).toLocaleDateString('en-ZA');
};

export const formatWeight = (weight?: number): string => {
  if (!weight) return 'N/A';
  return `${weight} kg`;
};

// Equipment purchase transaction helper
export const createEquipmentPurchaseTransaction = async (
  equipmentId: string,
  purchasePrice: number,
  supplierId?: string,
  supplierName?: string,
  supplierPhone?: string,
  supplierAddress?: string,
  paymentMethod: string = 'CASH',
  purchaseDate: Date = new Date(),
  notes?: string
) => {
  const { transactionsApi } = await import('./transactions');
  
  return transactionsApi.processInventoryPurchase({
    items: [{
      type: 'EQUIPMENT',
      itemId: equipmentId,
      quantity: 1,
      unitPrice: purchasePrice,
    }],
    supplierId,
    supplierName: supplierName || 'Unknown Supplier',
    supplierPhone: supplierPhone || '',
    supplierAddress: supplierAddress || '',
    paymentMethod: paymentMethod as any,
    purchaseDate,
    notes,
  });
};

// API functions
export const equipmentApi = {
  // Get equipment (alias for getAll)
  getEquipment: async (filters: EquipmentFilters = {}): Promise<EquipmentResponse> => {
    const params = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString());
      }
    });

    const response = await apiClient.get<EquipmentResponse>(`/equipment?${params.toString()}`);
    return response.data;
  },

  // Get all equipment with filters
  getAll: async (filters: EquipmentFilters = {}): Promise<EquipmentResponse> => {
    const params = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString());
      }
    });

    const response = await apiClient.get<EquipmentResponse>(`/equipment?${params.toString()}`);
    return response.data;
  },

  // Get equipment by ID
  getById: async (id: string): Promise<Equipment> => {
    const response = await apiClient.get<Equipment>(`/equipment/${id}`);
    return response.data;
  },

  // Create equipment
  create: async (data: CreateEquipmentDto): Promise<Equipment> => {
    const response = await apiClient.post<Equipment>('/equipment', data);
    return response.data;
  },

  // Update equipment
  update: async (id: string, data: UpdateEquipmentDto): Promise<Equipment> => {
    const response = await apiClient.patch<Equipment>(`/equipment/${id}`, data);
    return response.data;
  },

  // Delete equipment
  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/equipment/${id}`);
  },

  // Get statistics
  getStatistics: async (): Promise<EquipmentStats> => {
    const response = await apiClient.get<EquipmentStats>('/equipment/statistics');
    return response.data;
  },

  // Get equipment with maintenance due
  getMaintenanceDue: async (): Promise<Equipment[]> => {
    const response = await apiClient.get<Equipment[]>('/equipment/maintenance-due');
    return response.data;
  },

  // Get equipment with overdue maintenance
  getMaintenanceOverdue: async (): Promise<Equipment[]> => {
    const response = await apiClient.get<Equipment[]>('/equipment/maintenance-overdue');
    return response.data;
  },

  // Get equipment under warranty
  getUnderWarranty: async (): Promise<Equipment[]> => {
    const response = await apiClient.get<Equipment[]>('/equipment/under-warranty');
    return response.data;
  },

  // Get equipment by type
  getByType: async (type: EquipmentType): Promise<Equipment[]> => {
    const response = await apiClient.get<Equipment[]>(`/equipment/by-type/${type}`);
    return response.data;
  },

  // Record maintenance
  recordMaintenance: async (data: MaintenanceRecord): Promise<Equipment> => {
    const response = await apiClient.post<Equipment>('/equipment/maintenance', data);
    return response.data;
  },
}; 